import React, { useRef, useState } from "react";
import { adminFetch } from "./auth";
import { uid, resolveSrc, Toggle } from "./editorControls";

export function validateEvents(events) {
  const errors = [];
  events.forEach((e, i) => {
    const n = i + 1;
    if (!e.title || !e.title.trim()) errors.push(`Event #${n}: a title is required.`);
    if (e.startDate && e.endDate && new Date(e.endDate) < new Date(e.startDate)) {
      errors.push(`Event "${e.title || n}": end date is before the start date.`);
    }
  });
  return errors;
}

function EventImage({ image, onChange, onStatus, index }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const img = image || {};
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const d = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      onChange({ src: d.url, alt: img.alt || "" });
      onStatus && onStatus("success", "Image uploaded. Save Draft, then Publish.");
    } catch (err) {
      onStatus && onStatus("error", err.message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="event-image-field">
      <div className="admin-hero-preview" style={{ maxWidth: 220 }}>
        {img.src ? <img src={resolveSrc(img.src)} alt={img.alt || "event"} /> : null}
      </div>
      <input value={img.alt || ""} onChange={(e) => onChange({ ...img, alt: e.target.value })} placeholder="Image alt text" data-testid={`event-image-alt-${index}`} />
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} data-testid={`event-image-file-${index}`} />
      <button className="admin-mini" disabled={busy} onClick={() => ref.current?.click()} data-testid={`event-image-upload-${index}`}>
        {busy ? "Uploading…" : img.src ? "Replace Image" : "Upload Image"}
      </button>
      {img.src ? (
        <button className="admin-mini danger" onClick={() => onChange({ src: null, alt: "" })} data-testid={`event-image-remove-${index}`}>Remove Image</button>
      ) : null}
    </div>
  );
}

export function EventsEditor({ value, onChange, onStatus }) {
  const events = value || [];
  const set = (i, k, v) => onChange(events.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= events.length) return;
    const copy = [...events];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy.map((e, idx) => ({ ...e, order: idx })));
  };
  const remove = (i) => {
    if (window.confirm(`Delete the event "${events[i].title || "Untitled"}"? This cannot be undone.`)) {
      onChange(events.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, order: idx })));
    }
  };
  const add = () => {
    onChange([
      ...events,
      { id: uid(), title: "", date: "", description: "", location: "", link: "/golf-events", image: null, startDate: "", endDate: "", startTime: "", expirationDate: "", featured: false, visible: true, order: events.length },
    ]);
  };

  const isExpired = (e) => {
    if (!e.expirationDate) return false;
    const t = new Date(e.expirationDate).getTime();
    return !Number.isNaN(t) && t + 86400000 < Date.now();
  };

  return (
    <section className="admin-section admin-section-wide" data-testid="events-editor">
      <h2>Events &amp; Activities</h2>
      <p className="admin-hint">
        Featured, visible, non-expired events appear on the homepage (top 3 by order). Expired events are
        hidden automatically but not deleted. Drag order with the arrows.
      </p>

      {events.map((ev, i) => (
        <div className={`admin-card-editor event-editor-card ${isExpired(ev) ? "is-expired" : ""}`} key={ev.id || i} data-testid={`event-card-${i}`}>
          <div className="event-editor-head">
            <span className="event-order-badge">#{i + 1}</span>
            <div className="event-tags">
              {ev.featured ? <span className="event-tag tag-featured">Featured</span> : null}
              {ev.visible === false ? <span className="event-tag tag-hidden">Unpublished</span> : <span className="event-tag tag-live">Visible</span>}
              {isExpired(ev) ? <span className="event-tag tag-expired">Expired</span> : null}
            </div>
            <div className="event-reorder">
              <button className="admin-mini" onClick={() => move(i, -1)} data-testid={`event-up-${i}`}>↑</button>
              <button className="admin-mini" onClick={() => move(i, 1)} data-testid={`event-down-${i}`}>↓</button>
            </div>
          </div>

          <input value={ev.title || ""} onChange={(e) => set(i, "title", e.target.value)} placeholder="Event title (required)" data-testid={`event-title-${i}`} />
          <input value={ev.date || ""} onChange={(e) => set(i, "date", e.target.value)} placeholder='Display date label, e.g. "March 9" or "Wednesdays 9:30 AM"' data-testid={`event-date-${i}`} />
          <textarea rows={2} value={ev.description || ""} onChange={(e) => set(i, "description", e.target.value)} placeholder="Description" data-testid={`event-desc-${i}`} />

          <div className="event-grid-3">
            <label className="event-mini-label">Start date
              <input type="date" value={ev.startDate || ""} onChange={(e) => set(i, "startDate", e.target.value)} data-testid={`event-startdate-${i}`} />
            </label>
            <label className="event-mini-label">End date
              <input type="date" value={ev.endDate || ""} onChange={(e) => set(i, "endDate", e.target.value)} data-testid={`event-enddate-${i}`} />
            </label>
            <label className="event-mini-label">Start time
              <input type="time" value={ev.startTime || ""} onChange={(e) => set(i, "startTime", e.target.value)} data-testid={`event-starttime-${i}`} />
            </label>
          </div>
          <div className="event-grid-3">
            <label className="event-mini-label">Expires on (optional — auto-hides after)
              <input type="date" value={ev.expirationDate || ""} onChange={(e) => set(i, "expirationDate", e.target.value)} data-testid={`event-expiration-${i}`} />
            </label>
            <label className="event-mini-label">Location
              <input value={ev.location || ""} onChange={(e) => set(i, "location", e.target.value)} placeholder="e.g. Main Course" data-testid={`event-location-${i}`} />
            </label>
            <label className="event-mini-label">Link when clicked
              <input value={ev.link || ""} onChange={(e) => set(i, "link", e.target.value)} placeholder="/golf-events or https://..." data-testid={`event-link-${i}`} />
            </label>
          </div>

          <EventImage image={ev.image} onChange={(img) => set(i, "image", img)} onStatus={onStatus} index={i} />

          <div className="event-toggles">
            <Toggle label="Featured on homepage" checked={!!ev.featured} onChange={(c) => set(i, "featured", c)} testid={`event-featured-${i}`} />
            <Toggle label="Published (visible to public)" checked={ev.visible !== false} onChange={(c) => set(i, "visible", c)} testid={`event-visible-${i}`} />
          </div>

          <button className="admin-mini danger" onClick={() => remove(i)} data-testid={`event-delete-${i}`}>Delete Event</button>
        </div>
      ))}

      <button className="admin-mini" onClick={add} data-testid="event-add">+ Add Event</button>
    </section>
  );
}
